import React, { useEffect, useState, useRef } from "react";
import httpClient from "../../_util/api";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, Autocomplete
} from "@mui/material";
import { ReceiptLong } from "@mui/icons-material";
import ButtonMain from "../../components/Button";
import ApplicationView from "../ReveiwerDashboard/ApplicationView";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import { hideLoader, showLoader } from "../../Store/mainSlice";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../_util/UseDebounce";

const Applications = () => {
  const dispatch = useDispatch();
  const userdetail = useSelector((state) => state.mainSlice.userdetail);
  const isSuperAdmin = (userdetail?.role || "").toLowerCase() === "superadmin";
  const snackbar_Ref = useRef();
  const [data, setData] = useState([]);
  const [isViewDetails, setIsViewDetails] = useState(false);
  const [applicationData, setApplicationData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [actionStatus, setActionStatus] = useState("");
  const [vendor, setVendor] = useState("");
  const [percent, setPercent] = useState(100);
  const [otherAmount, setOtherAmount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalApplications, setTotalApplications] = useState(0);
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const latestRequestRef = useRef(0);
  const getAllApplications = async () => {
    // Only the most recent request may update the table; an older, slower
    // response (e.g. a shorter search term) must not overwrite newer results.
    const requestId = ++latestRequestRef.current;
    dispatch(showLoader());
    try {
      const searchParam = (debouncedSearch && debouncedSearch.trim()) ? debouncedSearch.trim() : (selectedUniversity ?? "");
      const res = await httpClient.get('/students/getAllApplications', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchParam
        }
      });
      if (requestId !== latestRequestRef.current) return;
      if (res.status === 200) {
        setData(res.data.applications);
        setTotalApplications(res.data.pagination.totalApplications || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // Wait for the user to stop typing before searching, and start from the first page.
  useDebounce(() => {
    setDebouncedSearch(search);
    setPage(0);
  }, [search], 500);

  useEffect(() => {
    getAllApplications();
  }, [page, rowsPerPage, debouncedSearch, selectedUniversity]);

  const getVendorListHandler = async () => {
    try {
      const res = await httpClient.get(`/user/role/vendor`);
      if (res.status === 200) setVendorList(res.data || []);
    } catch (e) {
      console.error("Error fetching vendors:", e);
    }
  };

  useEffect(() => {
    if (isViewDetails) getVendorListHandler();
  }, [isViewDetails]);

  useEffect(() => {
    httpClient.get('students/getAllPakUniversities').then((res) => {
      if (res?.status === 200)
        setUniversities(['Other', ...res.data.map((item) => item.name)]);
    }).catch(() => {});
  }, []);

  // When reviewer/superadmin views an application on all-applications, PATCH to update faculty/otherFaculty (same as apply-scholarship)
  useEffect(() => {
    const patchApplicationOnView = async () => {
      const appId = applicationData?.applicationId || applicationData?._id;
      if (!isViewDetails || !appId) return;
      const faculty = applicationData.faculty !== undefined ? applicationData.faculty : "";
      const otherFaculty = applicationData.otherFaculty !== undefined ? applicationData.otherFaculty : "";
      try {
        await httpClient.patch(`/students/application/${appId}`, {
          faculty,
          otherFaculty,
        });
      } catch (error) {
        console.error("Error updating application on view:", error);
      }
    };
    patchApplicationOnView();
  }, [isViewDetails, applicationData?.applicationId, applicationData?._id]);

  const currentStatus = (applicationData?.status || "").toLowerCase();
  const currentVendorId = applicationData?.vendor?.id ? String(applicationData.vendor.id) : "";

  const STATUS_LABELS = {
    pending: "Pending",
    process: "Reviewed",
    approved: "Approved",
    paid: "Paid",
    rejected: "Rejected",
  };

  // Current status first (preselected), then the statuses it can move to.
  const statusOptions = [
    ...(currentStatus ? [{ value: currentStatus, label: `${STATUS_LABELS[currentStatus] || currentStatus} (current)` }] : []),
    ...(isSuperAdmin && currentStatus !== "approved" && currentStatus !== "paid"
      ? [{ value: "approved", label: "Approved" }] : []),
    ...(currentStatus !== "rejected" ? [{ value: "rejected", label: "Rejected" }] : []),
    { value: "deleted", label: "Deleted" },
  ];

  const openActionModal = () => {
    setActionStatus(currentStatus);
    setVendor(currentVendorId);
    setRemarks("");
    setIsModalOpen(true);
  };

  // Approving from another status (superadmin) vs. only reassigning the partner of an approved one.
  const isApproving = isSuperAdmin && actionStatus === "approved" && currentStatus !== "approved";
  const isPartnerChange = actionStatus === "approved" && currentStatus === "approved" && vendor !== currentVendorId;
  const hasChanges = actionStatus !== currentStatus || isPartnerChange;

  const selectedVendor = vendorList.find((v) => String(v._id) === vendor);

  const vendorData = selectedVendor
    ? { id: selectedVendor._id, name: selectedVendor.firstName, status: "pending" }
    : applicationData?.vendor || {};

  const approvedFee =
    percent === "other"
      ? Number(otherAmount) || 0
      : Math.round(((applicationData?.tuttionFee || 0) * Number(percent)) / 100);

  const handleChangePartner = async () => {
    dispatch(showLoader());
    const res = await httpClient
      .patch(`/students/application/${applicationData?._id}/partner`, { vendorId: vendor, remarks })
      .catch((err) => err?.response);
    dispatch(hideLoader());
    if (res?.status === 200) {
      setIsModalOpen(false);
      setApplicationData((prev) => ({ ...prev, vendor: { ...(prev?.vendor || {}), id: vendor } }));
      snackbar_Ref.current?.showMessage("success", res.data.message, "", "i-chk-circle");
      getAllApplications();
    } else {
      snackbar_Ref.current?.showMessage("error", res?.data?.message || "Update failed", "", "i-chk-circle");
    }
  };

  const handleSubmitAction = async () => {
    if (!hasChanges) return;
    if (isPartnerChange) return handleChangePartner();
    const applicationId = applicationData?.applicationId;
    if (isApproving && !selectedVendor) {
      snackbar_Ref.current?.showMessage("error", "Please select a vendor to assign", "", "i-chk-circle");
      return;
    }
    dispatch(showLoader());
    const payload = {
      applicationId,
      userId: applicationData?.userId,
      applicationNumber: applicationData.applicationNumber,
      status: actionStatus === "approved" ? "approved" : actionStatus,
      vendor: actionStatus === "rejected" ? (applicationData.vendor || {}) : vendorData,
      remarks: remarks || "",
      ...(actionStatus === "approved" && {
        approvedFee,
        amount: approvedFee,
      }),
    };
    const res = await httpClient.post("/students/approveStudent", payload).catch((err) => err?.response);
    dispatch(hideLoader());
    if (res?.status === 200) {
      setIsModalOpen(false);
      snackbar_Ref.current?.showMessage("success", res.data.message, "", "i-chk-circle");
      if (actionStatus === "deleted") {
        setIsViewDetails(false);
        setApplicationData(null);
      } else {
        setApplicationData((prev) => ({ ...prev, status: payload.status }));
      }
      getAllApplications();
    } else {
      snackbar_Ref.current?.showMessage(
        "error",
        res?.data?.message || "Update failed",
        "",
        "i-chk-circle"
      );
    }
  };

  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

   if (isViewDetails && applicationData) {
      return (
        <>
        <CustomizedSnackbars ref={snackbar_Ref} />
        <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2} m={2} flexWrap="wrap">
          <ButtonMain onClick={openActionModal}>
            Take Action
          </ButtonMain>
          <ButtonMain variant="white" onClick={() => setIsViewDetails(false)}>
            Back to List
          </ButtonMain>
        </Box>
        {applicationData?.vendor?.challanFile?.url && (
          <Box
            sx={{
              p: 2, mx: 2, mb: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
              bgcolor: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 2, color: "#1b5e20",
            }}
          >
            <ReceiptLong sx={{ color: "#2e7d32" }} />
            <strong>Paid Challan</strong>
            <Box
              component="a"
              href={applicationData.vendor.challanFile.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ml: "auto", px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: "#fff",
                border: "1px solid #a5d6a7", color: "#2e7d32", fontWeight: 600, textDecoration: "none",
                "&:hover": { bgcolor: "#c8e6c9" },
              }}
            >
              {applicationData.vendor.challanFile.fileName || "View challan"}
            </Box>
          </Box>
        )}
        <Paper>
          <ApplicationView
            data={applicationData}
            editable
            applicationId={applicationData?._id}
            universities={universities}
            editApprovedFee
            onSave={async (fields) => {
              const res = await httpClient
                .patch(`/students/application/${applicationData?._id}`, fields)
                .catch((err) => err?.response);
              if (res?.status === 200) {
                setApplicationData((prev) => ({ ...prev, ...fields }));
                snackbar_Ref.current?.showMessage("success", "Application updated successfully", "", "i-chk-circle");
              } else {
                snackbar_Ref.current?.showMessage("error", res?.data?.message || "Update failed", "", "i-error-circle");
                throw new Error(res?.data?.message || "Update failed");
              }
            }}
          />
        </Paper>
        <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Update application status</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={actionStatus}
                onChange={(e) => setActionStatus(e.target.value)}
                label="Status"
              >
                {statusOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isApproving && (
              <>
              <FormControl fullWidth margin="normal">
                <InputLabel id="percent-label-sa">Fee Approved in Percent</InputLabel>
                <Select
                  labelId="percent-label-sa"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  label="Fee Approved in Percent"
                >
                  {[25, 50, 75, 100].map((val) => (
                    <MenuItem key={val} value={val}>{val}%</MenuItem>
                  ))}
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              {percent === "other" && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Amount"
                  type="number"
                  value={otherAmount}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || Number(v) <= (applicationData?.tuttionFee || 0)) setOtherAmount(v);
                  }}
                />
              )}
              <TextField
                fullWidth
                margin="normal"
                label="Fee summary"
                value={`Tuition: ${applicationData?.tuttionFee ?? "-"} | Approved fee: ${approvedFee}`}
                InputProps={{ readOnly: true }}
              />
              </>
            )}
            {actionStatus === "approved" && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Partner</InputLabel>
                <Select
                  value={selectedVendor ? vendor : ""}
                  onChange={(e) => setVendor(e.target.value)}
                  label="Partner"
                >
                  {vendorList.map((v) => (
                    <MenuItem key={v._id} value={String(v._id)}>
                      {v.firstName}{String(v._id) === currentVendorId ? " (current)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Remarks"
              multiline
              rows={4}
              fullWidth
              margin="normal"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <ButtonMain variant="white" onClick={() => setIsModalOpen(false)}>Cancel</ButtonMain>
            <ButtonMain onClick={handleSubmitAction} disabled={!actionStatus || !hasChanges}>Submit</ButtonMain>
          </DialogActions>
        </Dialog>
        </>
      );
    }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          label="Search by Email or Application Number"
          placeholder="Email or application number..."
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          sx={{ minWidth: 280 }}
        />
        <Autocomplete
          options={universities}
          value={selectedUniversity}
          onChange={(event, newValue) => {
            setSelectedUniversity(newValue);
            setPage(0);
          }}
          sx={{ width: 300 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Institution Name"
              variant="outlined"
            />
          )}
        />
      </Box>
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#4caf50" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell> 
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Application No</TableCell> 
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>CNIC</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Enrollment #</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Tuition Fee</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Approved Fee</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Submission Date</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paid Challan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                key={item._id}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setApplicationData(data.find(e => e._id == item._id))
                  setIsViewDetails(true); 
                }} 
              >
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{item.applicationNumber}</TableCell>
                <TableCell>{item.firstName} {item.lastName}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{item.CNICNo || "-"}</TableCell>
                <TableCell>{item.enrollmentNo || "-"}</TableCell>
                <TableCell>{item.tuttionFee || "-"}</TableCell>
                <TableCell>{item.approvedFee}</TableCell>
                <TableCell>{item.applicationSubmitDate}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {item.vendor?.challanFile?.url ? (
                    <a href={item.vendor.challanFile.url} target="_blank" rel="noopener noreferrer">
                      View
                    </a>
                  ) : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalApplications}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default Applications;
